import React from 'react'

import { nutrimaticLabels } from '@/nutrimatic'
import { PairState } from '../../../../domain/multicall'
import { MintField as Field } from '../../../../domain/liquidity'
import type { AddLiquidityControllerResult } from '../../../../features/liquidity/useAddLiquidityController'
import { useLiquidityPositionMetrics } from '../../../../features/liquidity/useLiquidityPositionMetrics'
import { useAppWeb3 } from '../../../../hooks'
import { JSBI } from '../../../../sdk'
import {
  CompactMetricCell,
  CompactMetricsGrid,
  MetricDetailRow,
  MetricsDetailCard,
} from './MobileLiquidityMetricsLayout'
import { formatSharePercent, useProjectedPoolShare } from './liquidityMetricsUtils'

/** Hero de tasa bajo el título — mismo patrón que MobileSwapRateHero. */
export function MobileAddLiquidityRateHero({ controller }: { controller: AddLiquidityControllerResult }) {
  const { currencies, pairState, price } = controller
  const currencyA = currencies[Field.CURRENCY_A]
  const currencyB = currencies[Field.CURRENCY_B]

  if (!currencyA || !currencyB || pairState === PairState.INVALID) {
    return (
      <p className="nm-mobile-simple__rate-hero nm-mobile-simple__rate-hero--idle">
        {nutrimaticLabels.mobileSimple.rateIdle}
      </p>
    )
  }

  if (!price) {
    return (
      <p className="nm-mobile-simple__rate-hero nm-mobile-simple__rate-hero--idle">
        {nutrimaticLabels.mobileSimple.liquidityMetricsIdle}
      </p>
    )
  }

  return (
    <p className="nm-mobile-simple__rate-hero">
      1 {currencyA.symbol} = {price.toSignificant(6)} {currencyB.symbol}
    </p>
  )
}

/** Vista compacta bajo inputs — sin duplicar montos ni detalle de revisión. */
export function MobileAddLiquidityQuotePreview({ controller }: { controller: AddLiquidityControllerResult }) {
  const { account } = useAppWeb3()
  const { currencies, liquidityMinted, noLiquidity, pair, pairState, parsedAmounts, poolTokenPercentage } =
    controller

  const currencyA = currencies[Field.CURRENCY_A]
  const currencyB = currencies[Field.CURRENCY_B]
  const position = useLiquidityPositionMetrics(pair ?? null, account)
  const projectedShare = useProjectedPoolShare(pair ?? undefined, liquidityMinted, account)

  const hasAmounts = Boolean(parsedAmounts[Field.CURRENCY_A] && parsedAmounts[Field.CURRENCY_B])
  const hasExistingPosition =
    Boolean(position.poolShare) &&
    !JSBI.equal(position.poolShare!.numerator, JSBI.BigInt(0)) &&
    position.lpBalance !== '0'

  if (!currencyA || !currencyB || pairState === PairState.INVALID) {
    return (
      <p className="nm-mobile-simple__quote-preview nm-mobile-simple__quote-preview--idle px-4 pt-2">
        {nutrimaticLabels.mobileSimple.rateIdle}
      </p>
    )
  }

  if (!hasAmounts) {
    if (account && hasExistingPosition) {
      return (
        <CompactMetricsGrid>
          <CompactMetricCell
            label={nutrimaticLabels.mobileSimple.currentPoolShare}
            value={formatSharePercent(position.poolShare)}
          />
          <CompactMetricCell label={nutrimaticLabels.mobileSimple.lpBalance} value={position.lpBalance} />
        </CompactMetricsGrid>
      )
    }

    return (
      <p className="nm-mobile-simple__quote-preview nm-mobile-simple__quote-preview--idle px-4 pt-2">
        {!account
          ? nutrimaticLabels.mobileSimple.liquidityPositionConnectHint
          : nutrimaticLabels.mobileSimple.liquidityMetricsIdle}
      </p>
    )
  }

  const projectedLabel = formatSharePercent(
    projectedShare ?? (noLiquidity ? undefined : poolTokenPercentage),
  )

  return (
    <CompactMetricsGrid>
      {liquidityMinted ? (
        <CompactMetricCell
          label={nutrimaticLabels.mobileSimple.lpToReceive}
          value={liquidityMinted.toSignificant(4)}
          accent
        />
      ) : null}
      <CompactMetricCell
        label={
          hasExistingPosition
            ? nutrimaticLabels.mobileSimple.projectedPoolShare
            : nutrimaticLabels.mobileSimple.poolShare
        }
        value={noLiquidity ? '100%' : projectedLabel}
        accent={!liquidityMinted}
      />
      {hasExistingPosition ? (
        <>
          <CompactMetricCell
            label={nutrimaticLabels.mobileSimple.currentPoolShare}
            value={formatSharePercent(position.poolShare)}
          />
          <CompactMetricCell
            label={nutrimaticLabels.mobileSimple.lpBalance}
            value={position.lpBalance}
          />
        </>
      ) : null}
    </CompactMetricsGrid>
  )
}

/** Detalle completo — solo en pantalla de revisión. */
export function MobileAddLiquidityReviewDetails({ controller }: { controller: AddLiquidityControllerResult }) {
  const { account } = useAppWeb3()
  const {
    allowedSlippage,
    currencies,
    liquidityMinted,
    noLiquidity,
    pair,
    parsedAmounts,
    poolTokenPercentage,
    price,
  } = controller

  const currencyA = currencies[Field.CURRENCY_A]
  const currencyB = currencies[Field.CURRENCY_B]
  const position = useLiquidityPositionMetrics(pair ?? null, account)
  const projectedShare = useProjectedPoolShare(pair ?? undefined, liquidityMinted, account)
  const hasExistingPosition =
    Boolean(position.poolShare) &&
    !JSBI.equal(position.poolShare!.numerator, JSBI.BigInt(0)) &&
    position.lpBalance !== '0'

  return (
    <div className="space-y-3">
      <MetricsDetailCard title={nutrimaticLabels.mobileSimple.synthesisSummaryTitle}>
        <MetricDetailRow
          label={nutrimaticLabels.mobileSimple.depositAmount(currencyA?.symbol ?? '')}
          value={parsedAmounts[Field.CURRENCY_A]?.toSignificant(6) ?? '—'}
        />
        <MetricDetailRow
          label={nutrimaticLabels.mobileSimple.depositAmount(currencyB?.symbol ?? '')}
          value={parsedAmounts[Field.CURRENCY_B]?.toSignificant(6) ?? '—'}
        />
        {liquidityMinted ? (
          <MetricDetailRow
            label={nutrimaticLabels.mobileSimple.lpToReceive}
            value={liquidityMinted.toSignificant(6)}
          />
        ) : null}
        {hasExistingPosition ? (
          <>
            <MetricDetailRow
              label={nutrimaticLabels.mobileSimple.currentPoolShare}
              value={formatSharePercent(position.poolShare)}
            />
            <MetricDetailRow label={nutrimaticLabels.mobileSimple.lpBalance} value={position.lpBalance} />
            <MetricDetailRow
              label={nutrimaticLabels.mobileSimple.positionTokenAmount(currencyA?.symbol ?? '')}
              value={position.token0Amount}
            />
            <MetricDetailRow
              label={nutrimaticLabels.mobileSimple.positionTokenAmount(currencyB?.symbol ?? '')}
              value={position.token1Amount}
            />
          </>
        ) : null}
        <MetricDetailRow
          label={
            hasExistingPosition
              ? nutrimaticLabels.mobileSimple.projectedPoolShare
              : nutrimaticLabels.mobileSimple.poolShare
          }
          value={
            noLiquidity
              ? '100%'
              : formatSharePercent(projectedShare ?? poolTokenPercentage)
          }
        />
      </MetricsDetailCard>

      {price && currencyA && currencyB ? (
        <MetricsDetailCard title={nutrimaticLabels.mobileSimple.poolRatesTitle}>
          <MetricDetailRow
            label={nutrimaticLabels.mobileSimple.exchangeRate}
            value={`1 ${currencyA.symbol} = ${price.toSignificant(4)} ${currencyB.symbol}`}
          />
          <MetricDetailRow
            label={nutrimaticLabels.mobileSimple.poolRateLabel(currencyB.symbol ?? '', currencyA.symbol ?? '')}
            value={price.invert().toSignificant(6)}
          />
          <MetricDetailRow label="Slippage" value={`${allowedSlippage / 100}%`} />
        </MetricsDetailCard>
      ) : null}
    </div>
  )
}
